import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { ApiError } from '../types/api';

// Create axios instance with base configuration
const apiClient: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for adding session headers
apiClient.interceptors.request.use(
  (config) => {
    // Add session ID from sessionStorage
    const sessionId = sessionStorage.getItem('sessionId');
    if (sessionId) {
      config.headers['X-Session-ID'] = sessionId;
    }
    
    // Enable credentials for cookie-based authentication (shared with TWAS)
    config.withCredentials = true;
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling common errors
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error: AxiosError) => {
    let apiError: ApiError = {
      message: 'An unexpected error occurred',
      status: 500,
    };

    if (error.response) {
      // Server responded with error status
      apiError = {
        message: (error.response.data as any)?.message || error.message,
        status: error.response.status,
        code: (error.response.data as any)?.code,
      };

      // Handle 401 Unauthorized - redirect to login
      if (error.response.status === 401) {
        console.log('Session expired or invalid. Redirecting to login...');
        
        // Clear session data
        sessionStorage.removeItem('sessionId');
        sessionStorage.removeItem('userData');
        
        // Redirect to login page (only if not already there)
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    } else if (error.request) {
      // Request was made but no response received
      apiError = {
        message: 'No response from server. Please check your connection.',
        status: 0,
      };
    } else {
      // Something else happened
      apiError = {
        message: error.message,
        status: 500,
      };
    }

    console.error('API Error:', apiError);
    return Promise.reject(apiError);
  }
);

export default apiClient;
